import React, { useState } from "react";

import * as Axios from "providers/axios";
import * as Cognito from "providers/cognito";

interface UserContextInterface {
  permissions: {
    isSuperAdmin: boolean;
    isOwner: boolean;
    isManager: boolean;
    isArtist: boolean;
    isUser: boolean;
    role: { id: string; role: string; company_id: string; user_id: string };
    companyId: string | undefined;
    isCompanyVerified: boolean;
    isCompanyLimited: boolean;
    isCompanyBasic: boolean;
    isCompanyFull: boolean;
    isCompanyWithoutStatus: boolean;
    impersonateLocationIds: string[];
    impersonateCompanyData: any | null;
  };
  updateUser: (value: any) => any;
  getUser: () => any;
  user: any | null;
  showOnboarding: boolean;
  setShowOnboarding: React.Dispatch<React.SetStateAction<boolean>>;
  isNewChargeModalOpen: boolean;
  setIsNewChargeModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface Props {
  children: React.ReactNode;
}

const UserStateContext = React.createContext<UserContextInterface | undefined>(
  undefined
);

function UserProvider(props: Props) {
  const { children } = props;
  const { api } = Axios.useAxios();
  const { user: cognitoUser } = Cognito.useCognito();
  const [user, setUser] = React.useState<any | null>(undefined);
  const [impersonateLocationIds, setImpersonateLocationIds] = React.useState(
    []
  );
  const [impersonateCompanyData, setImpersonateCompanyData] =
    React.useState<any>(null);
  const [showOnboarding, setShowOnboarding] = useState<boolean>(false);
  const [isNewChargeModalOpen, setIsNewChargeModalOpen] =
    useState<boolean>(false);

  const getUser = async function () {
    try {
      if (cognitoUser?.email) {
        const response = await api
          .get(`/v1/users/${cognitoUser.email}`)
          .then((r) => r.data);

        if (response?.id) {
          if (response.impersonate_company_id) {
            const impersonateCompany = await api
              .get(
                `/v1/companies/impersonate/${response.impersonate_company_id}`
              )
              .then((r) => r.data);

            const locationIds = impersonateCompany?.locations?.map(
              (location: any) => location.id
            );
            setImpersonateLocationIds(locationIds);
            setImpersonateCompanyData(impersonateCompany);
          }
          return setUser(response);
        }
        setUser({});
      }

      setUser({});
    } catch (err) {
      return;
    }
  };

  React.useEffect(() => {
    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, cognitoUser?.email]);

  const impersonatedUserLogout = async () => {
    try {
      await api.post("v1/users/impersonate-logout");
      setUser((prev: any) => ({
        ...prev,
        impersonate_id: null,
        impersonate_name: null
      }));
    } catch (error) {
      throw new Error("Error logging out: " + error.message);
    }
  };

  React.useEffect(() => {
    // @ts-ignore
    if (window?.ReactNativeWebView) {
      if (user && Object.keys(user).length !== 0) {
        const userInfo = {
          id: user.id,
          email: user.email,
          username: `${user.first_name} ${user.last_name}`,
          company_id: user.company?.id
        };
        const action = {
          type: "user",
          payload: userInfo
        };
        // @ts-ignore
        window?.ReactNativeWebView?.postMessage(JSON.stringify(action));
      }
    }
    if (user && user.impersonate_id && !user.impersonate_company_id) {
      impersonatedUserLogout();
    }
    if (
      user?.company?.locations?.length === 0 &&
      user?.joined_company?.locations?.length === 0 &&
      localStorage.getItem("onboardingGuideFinished") !== "true"
    ) {
      setShowOnboarding(true);
      // a18089be-066c-4da8-a58f-2a4d44852c86@mailslurp.net
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const updateUser = (user: any) => {
    setUser(user);

    return user;
  };

  const permissions = {
    isSuperAdmin: user?.role?.role === "SUPER",
    isOwner: user?.role?.role === "OWNER",
    isManager: user?.role?.role === "MANAGER",
    isArtist: user?.role?.role === "ARTIST",
    isUser: user?.role?.role === "USER",
    role: user?.role || "",
    // role: {
    //   id: "cca09373-c926-44d2-89c1-97c313383bc1",
    //   company_id: "a40f77ca-4ff0-48d9-87f2-3ad0e3a1151d",
    //   user_id: "5a76f2a0-bc5b-4cff-860d-a9a9bb3bd1da",
    //   created_at: "2024-07-21T19:46:46.650Z",
    //   updated_at: "2024-07-21T19:46:46.650Z",
    //   status: "ACTIVE",
    //   role: "OWNER"
    // },
    impersonateLocationIds:
      impersonateLocationIds.length > 0 ? impersonateLocationIds : [],
    impersonateCompanyData: impersonateCompanyData || null,
    companyId:
      user?.impersonate_company_id ||
      user?.company?.id ||
      user?.joined_company_id,
    isCompanyVerified: user?.company?.is_verified || false,
    isCompanyLimited: user?.company?.status === "LIMITED",
    isCompanyBasic: user?.company?.status === "BASIC",
    isCompanyFull: user?.company?.status === "FULL",
    isCompanyWithoutStatus: user?.company?.status === null
  };

  return (
    <UserStateContext.Provider
      value={{
        permissions,
        updateUser,
        getUser,
        user,
        showOnboarding,
        setShowOnboarding,
        isNewChargeModalOpen,
        setIsNewChargeModalOpen
      }}
    >
      {children}
    </UserStateContext.Provider>
  );
}

function useUser() {
  const context = React.useContext(UserStateContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export { UserProvider, useUser };
